import { Check, CircleCheck, Headphones, Radio, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge, dotTone } from '@/components/ui/badge';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

type DemoStatus = 'queued' | 'working' | 'review' | 'approval' | 'done';

const STATUS: Record<DemoStatus, { label: string; tone: 'neutral' | 'blue' | 'accent' | 'amber' | 'green'; live?: boolean }> = {
  queued: { label: t('Queued'), tone: 'neutral' },
  working: { label: t('Working'), tone: 'blue', live: true },
  review: { label: t('In review'), tone: 'accent', live: true },
  approval: { label: t('Needs you'), tone: 'amber', live: true },
  done: { label: t('Done'), tone: 'green' },
};

const ITEMS = [
  { key: 'CASE-123', title: t('Refund request for order #4471'), agent: 'Mia' },
  { key: 'CASE-124', title: t('Login fails after password reset'), agent: 'Max' },
  { key: 'CASE-125', title: t('Change invoice address'), agent: 'Mia' },
];

interface Step {
  status: [DemoStatus, DemoStatus, DemoStatus];
  event: string;
  approval?: 'pending' | 'approved';
}

/** One loop of a support desk: trigger fires, the Lead dispatches, agents work, one refund stops for a person. */
const STEPS: Step[] = [
  { status: ['queued', 'queued', 'queued'], event: t('Polling trigger found 3 new cases') },
  { status: ['working', 'queued', 'queued'], event: t('Lena assigned CASE-123 to Mia') },
  { status: ['working', 'working', 'queued'], event: t('Max is reproducing the login bug') },
  { status: ['approval', 'review', 'queued'], event: t('Mia asks you: refund €49.00?'), approval: 'pending' },
  { status: ['approval', 'done', 'working'], event: t('Architect approved Max’s fix'), approval: 'pending' },
  { status: ['working', 'done', 'working'], event: t('You approved the refund'), approval: 'approved' },
  { status: ['done', 'done', 'done'], event: t('3 cases resolved · avg. 6 min') },
];

const STEP_MS = 2600;

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/**
 * Animated product mock for the landing page. Pauses on hover; shows a still frame for reduced motion.
 * With `onLoopEnd` it plays once and hands over instead of looping.
 */
export function LiveDesk({ className, onLoopEnd }: { className?: string; onLoopEnd?: () => void }) {
  const reduced = usePrefersReducedMotion();
  const [step, setStep] = useState(reduced ? 3 : 0);
  const [paused, setPaused] = useState(false);
  const [log, setLog] = useState<string[]>([STEPS[0].event]);

  useEffect(() => {
    if (reduced || paused) return;
    const timer = setTimeout(() => {
      if (onLoopEnd && step === STEPS.length - 1) return onLoopEnd();
      const next = (step + 1) % STEPS.length;
      setStep(next);
      setLog((l) => (next === 0 ? [STEPS[0].event] : [STEPS[next].event, ...l].slice(0, 4)));
    }, STEP_MS);
    return () => clearTimeout(timer);
  }, [step, paused, reduced, onLoopEnd]);

  const current = STEPS[step];
  const doneCount = current.status.filter((s) => s === 'done').length;

  return (
    <div
      className={cn('relative rounded-2xl border bg-card/80 p-1.5 shadow-2xl shadow-primary/10 backdrop-blur', className)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label={t('Example: a support desk working through cases')}
      role="img"
    >
      <div className="overflow-hidden rounded-xl border bg-background">
        {/* Desk header */}
        <div className="flex items-center gap-3 border-b px-4 py-3">
          <div className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Headphones className="size-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{t('Customer Support')}</div>
            <div className="truncate text-xs text-muted-foreground">{t('Goal: every case answered within 48 h')}</div>
          </div>
          <Badge tone="green">
            <span className={cn('size-1.5 rounded-full', dotTone.green, 'animate-soft-pulse')} />
            {t('Live')}
          </Badge>
        </div>

        {/* Team */}
        <div className="flex items-center gap-2 border-b px-4 py-2.5 text-xs text-muted-foreground">
          <AgentChip name="Lena" role={t('Lead')} />
          <AgentChip name="Mia" role={t('Support')} />
          <AgentChip name="Max" role={t('Engineer')} />
          <span className="ml-auto hidden items-center gap-1 sm:inline-flex">
            <Radio className="size-3.5" /> {t('every 5 min')}
          </span>
        </div>

        {/* Work items */}
        <ul className="divide-y">
          {ITEMS.map((item, i) => {
            const meta = STATUS[current.status[i]];
            return (
              <li key={item.key} className="flex items-center gap-3 px-4 py-2.5">
                <span className="w-[68px] shrink-0 font-mono text-[11px] text-muted-foreground">{item.key}</span>
                <span className={cn('min-w-0 flex-1 truncate text-[13px] transition-colors', current.status[i] === 'done' && 'text-muted-foreground line-through decoration-muted-foreground/40')}>
                  {item.title}
                </span>
                <span className="hidden text-xs text-muted-foreground sm:inline">{item.agent}</span>
                <Badge tone={meta.tone} className="min-w-[88px] justify-center transition-colors duration-500">
                  <span className={cn('size-1.5 rounded-full', dotTone[meta.tone], meta.live && 'animate-soft-pulse')} />
                  {meta.label}
                </Badge>
              </li>
            );
          })}
        </ul>

        {/* Approval */}
        <div className={cn('grid transition-all duration-500 ease-out', current.approval ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0')}>
          <div className="overflow-hidden">
            <div className="m-3 rounded-lg border border-amber-300/60 bg-amber-50 p-3 dark:border-amber-800/60 dark:bg-amber-950/30">
              <div className="flex items-start gap-2.5">
                <ShieldCheck className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium">{t('Refund €49.00 to Acme GmbH?')}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{t('Mia checked the order: delivered damaged, photo attached. Playbook: refunds need approval.')}</div>
                </div>
              </div>
              <div className="mt-2.5 flex justify-end gap-2">
                {current.approval === 'approved' ? (
                  <span className="inline-flex h-7 items-center gap-1 rounded-md bg-emerald-600 px-2.5 text-xs font-medium text-white">
                    <Check className="size-3.5" /> {t('Approved by you')}
                  </span>
                ) : (
                  <>
                    <span className="inline-flex h-7 items-center rounded-md border bg-card px-2.5 text-xs font-medium">{t('Reject')}</span>
                    <span className="relative inline-flex h-7 items-center rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground">
                      <span className="absolute inset-0 animate-ping rounded-md bg-primary/40 [animation-duration:1.8s]" />
                      <span className="relative">{t('Approve')}</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity */}
        <div className="border-t bg-muted/40 px-4 py-2.5">
          <div className="mb-1.5 flex items-center justify-between text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {t('Activity')}
            <span className="inline-flex items-center gap-1 normal-case tracking-normal">
              <CircleCheck className="size-3 text-emerald-600 dark:text-emerald-400" /> {t('{n}/3 done', { n: doneCount })}
            </span>
          </div>
          <ul className="space-y-1">
            {log.slice(0, 3).map((line, i) => (
              <li
                key={`${line}-${log.length - i}`}
                className={cn('truncate text-xs transition-opacity', i === 0 ? 'animate-in fade-in slide-in-from-top-1 text-foreground duration-500' : 'text-muted-foreground', i === 2 && 'opacity-50')}
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function AgentChip({ name, role }: { name: string; role: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border bg-card py-0.5 pl-0.5 pr-2">
      <span className="flex size-5 items-center justify-center rounded-full bg-gradient-to-br from-primary/80 to-violet-500 text-[10px] font-semibold text-white">{name[0]}</span>
      <span className="font-medium text-foreground">{name}</span>
      <span className="hidden sm:inline">{role}</span>
    </span>
  );
}
